import type { User } from "models";
import { Role } from "models/Role";
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useState,
} from "react";
import { USER_MSG } from "strings";
import { UIMessage } from "./UI";

const accessMap = {
  [Role.DESIGN]: [Role.DESIGN],
  [Role.USER]: [Role.DESIGN],
  [Role.MEASURE_LITE]: [Role.MEASURE_LITE, Role.USER, Role.DESIGN],
  [Role.ADMIN]: [Role.ADMIN, Role.USER, Role.DESIGN],
  [Role.SALES]: [Role.SALES, Role.DESIGN, Role.ADMIN, Role.USER],
  [Role.CUST_SERV]: [Role.CUST_SERV, Role.SALES, Role.DESIGN],
  [Role.LATHAM_ADMIN]: [
    Role.LATHAM_ADMIN,
    Role.ADMIN,
    Role.SALES,
    Role.DESIGN,
    Role.CUST_SERV,
    Role.USER,
  ],
  [Role.BAI_ADMIN]: [
    Role.BAI_ADMIN,
    Role.LATHAM_ADMIN,
    Role.ADMIN,
    Role.SALES,
    Role.DESIGN,
    Role.CUST_SERV,
    Role.USER,
  ],
};

export function hasAccess(user: User, role: Role): boolean {
  return accessMap[user.role] && accessMap[user.role].includes(role);
}

export function hasSuperAccess(user?: User): boolean {
  return user
    ? user.role === Role.BAI_ADMIN ||
        user.role === Role.LATHAM_ADMIN ||
        user.role === Role.DESIGN ||
        user.role === Role.USER ||
        user.role === Role.CUST_SERV ||
        user.role === Role.ADMIN
    : false;
}

interface AuthContextType {
  user?: User;
  setUser: (user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({
  initialUser,
  children,
}: {
  initialUser?: User;
  children?: ReactNode;
}): ReactElement {
  const [user, setUser] = useState<User>(initialUser as User);

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// Role-based protected route
export function AuthRoute({
  role,
  children,
}: {
  role: Role;
  children: ReactNode;
}): ReactElement {
  const { user } = useAuth();
  return hasAccess(user as User, role) ? (
    <>{children}</>
  ) : (
    <UIMessage {...USER_MSG.ERROR_UNAUTH_ROUTE} />
  );
}

function login() {
  const scopes = [
    "openid",
    "profile",
    "email",
    "offline_access",
    "pool:bp:ra:dlr",
    "pool:bp:ro:dlr",
    "pool:bp:ua:dlr",
    "pool:bp:ro:dlrbrand",
    "pool:bp:ra:dlrbrand",
    "pool:bp:ro:dlrpart",
    "pool:bp:co:dlrpart",
    "pool:bp:do:dlrpart",
    "pool:bp:uo:dlrpart",
    "pool:bp:ro:emp",
    "pool:bp:ra:emp",
    "pool:bp:co:emp",
    "pool:bp:uo:emp",
    "pool:bp:do:emp",
    "pool:bp:ca:oplog",
    "pool:bp:ra:oplog",
    "pool:bp:ra:order",
    "pool:bp:ca:order",
    "pool:bp:ua:order",
    "pool:bp:ro:order",
    "pool:bp:ra:swatch",
    "pool:bp:co:prj",
    "pool:bp:ro:prj",
    "pool:bp:uo:prj",
    "pool:bp:ra:prj",
    "pool:bp:ra:prjmedia",
    "pool:bp:ca:prjmedia",
    "pool:bp:da:prjmedia",
    "pool:bp:ra:prjorder",
    "pool:bp:da:prjorder",
    "pool:bp:ua:prjorder",
    "pool:bp:da:prjquote",
    "pool:bp:ra:prjrsrc",
    "pool:bp:xo:submitprj",
    "pool:bp:xo:submitorder",
    "pool:bp:xa:reviewquote",
    "pool:bp:xa:revieworder",
    "pool:bp:xa:emailorder",
    "pool:bp:xa:submitquote",
    "pool:bp:xa:submitorder",
    "pool:bp:ra:prjfeat",
    "pool:bp:ra:prjimg",
    "pool:bp:xa:generatedxf",
    "pool:bp:ua:pooldetail",
    "pool:bp:pa:rsrc",
    "pool:bp:ra:rsrc",
    "pool:bp:da:rsrc",
    "pool:bp:pa:rsrcraw",
    "pool:bp:ra:rsrcraw",
    "pool:bp:ra:rsrcimg",
    "pool:bp:pa:rsrcfile",
    "pool:bp:ro:user",
    "pool:bp:ra:user",
    "pool:bp:po:user",
    "pool:bp:uo:user",
    "pool:bp:ca:user",
    "pool:bp:xa:billable",
    "pool:ma:xa:billable",
    "pool:bp:ra:logreport",
    "pool:ad:debug",
    "pool:ad:xa:copyprj",
    "pool:ad:da:prj",
    "pool:ad:po:dlr",
    "pool:ad:ra:dlrbrand",
    "pool:bp:ra:prjspec",
  ].join(" ");

  const loginUrl = getLoginRedirectUrl(scopes);
  window.location.assign(loginUrl);
}

function getLoginRedirectUrl(scopes: string): string {
  const redirectUrl = new URL("/authorize", `https://latham-dev.us.auth0.com`);

  const searchParams = redirectUrl.searchParams;
  searchParams.append("response_type", "code");
  searchParams.append("client_id", "u4n8kPo16IPxm7eVJ5LKvxeOU1Ja9cMx");
  searchParams.append("redirect_uri", "http://localhost:4000/api/authz/callback");
  searchParams.append("scope", scopes);

  return redirectUrl.toString();
}

function logout() {
  const logoutUrl = buildUrl(ApiLogoutPathname, getPort());
  window.location.assign(logoutUrl);
}

export const getPort = (): string | undefined => {
  return import.meta.env.VITE_SERVER_PORT;
};

export const buildUrl = (
  pathname?: string,
  port?: string,
  queryParams?: Record<string, string>,
): string => {
  const newUrl = new URL(window.location.href);
  if (port) {
    newUrl.port = port;
  }
  if (pathname) {
    newUrl.pathname = pathname;
  }
  if (queryParams) {
    for (const key in queryParams) {
      newUrl.searchParams.append(key, queryParams[key]);
    }
  } else {
    newUrl.search = window.location.search;
  }

  return newUrl.toString();
};

export const ApiCallbackPathname = "/api/authz/callback";
export const ApiLogoutPathname = "api/authz/logout";
export const ApiLoginPathname = "/api/authz/login";
